"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Printingfaq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What file formats do you accept for DTF printing?",
    answer: "We prefer high-resolution PNG files with transparent backgrounds at 300 DPI. This ensures the sharpest detail and most vibrant color reproduction for your apparel transfers."
  }, {
    question: "What is your standard turnaround time?",
    answer: "Our standard production time is 24-48 hours for most orders. We prioritize quick turnaround to keep your fashion brand or boutique moving without delays."
  }, {
    question: "Are your transfers durable after washing?",
    answer: "Yes. Our DTF prints are engineered for high elasticity and wash durability. When applied correctly with our recommended heat-press settings, they withstand repeated cycles without cracking or fading."
  }, {
    question: "Do you offer bulk order discounts?",
    answer: "Absolutely. We provide tiered pricing for larger runs. Please contact our sales team with your volume requirements so we can provide a competitive quote for your project."
  }, {
    question: "Can I apply these transfers to any fabric?",
    answer: "DTF transfers are incredibly versatile and work on cotton, polyester, blends, denim, and even nylon. They are perfect for t-shirts, hoodies, hats, and promotional accessories."
  }, {
    question: "Do you use eco-friendly materials?",
    answer: "Sustainability is core to our process. We source OEKO-TEX certified inks and films, ensuring our prints are safe for the wearer and have a lower environmental impact."
  }, {
    question: "What heat-press settings do you recommend?",
    answer: "We recommend pressing at 300°F (150°C) for 12-15 seconds with medium pressure. Detailed application guides are included with every order to ensure professional results."
  }, {
    question: "How do I start a custom order?",
    answer: "You can upload your designs directly through our shop page or reach out via our contact form. We will review your files and confirm your order details before production begins."
  }];
  return <section data-section-id="381" id="common-printing-questions" className="bg-background py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-[0.2em] text-secondary uppercase mb-6">
              Technical Support
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground tracking-tighter font-bold">
              Printing
              <br />
              <em className="italic text-primary font-normal">Insights</em>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-8 flex flex-col justify-end">
            <p className="font-default text-base md:text-lg text-secondary leading-relaxed max-w-md mb-6">
              Answers to common questions about our DTF process. Need more help? Get in touch.
            </p>
            <Link to="#" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-foreground hover:text-primary transition-colors duration-300 w-fit group">
              Start Your Order
              <ArrowRight className="size-3 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div ref={listRef} className={`max-w-[800px] mx-auto transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-t border-border last:border-b border-b-0 data-[state=open]:border-primary/20 group">
                <AccordionTrigger className="py-6 md:py-7 hover:no-underline [&>svg]:hidden text-left group/trigger">
                  <span className="font-default text-lg md:text-xl leading-snug text-foreground transition-colors duration-300 group-data-[state=open]:italic group-data-[state=open]:text-primary pr-6 font-semibold">
                    {faq.question}
                  </span>
                  <span className="relative flex items-center justify-center size-6 shrink-0 mt-1">
                    <span className="absolute h-px w-4 bg-foreground transition-colors duration-300 group-data-[state=open]:bg-primary" />
                    <span className="absolute h-4 w-px bg-foreground transition-all duration-300 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0 group-data-[state=open]:bg-primary" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pt-0 pr-12">
                  <p className="font-default text-base leading-[1.7] text-secondary">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}